import {
  Component, OnInit,
  ViewChild,
  OnDestroy
} from "@angular/core";
import { Phases, Phase } from "./output-interfaces/output-interfaces";
import { MatTabGroup } from '@angular/material/tabs';
import { EstimateService } from "../../estimate.service"
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { store } from "src/app/input/estimate/input-interfaces/input-interfaces";
import { MatSnackBar } from "@angular/material/snack-bar";


@Component({
  selector: 'app-estimate-output',
  templateUrl: './estimate-output.component.html',
  styleUrls: ['./estimate-output.component.scss']
})
export class EstimateOutputComponent implements OnInit, OnDestroy {

  // Mat Tabs
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;

  public projectHeadline$: Observable<string> = this.progress.projectHeadline.asObservable();
  public disablePage$ = this.progress.disablePage.asObservable()

  // Store / Progress
  public store$: Observable<store> = this.progress.store.asObservable()
  private storeSubscription: Subscription;
  private tabSubscription: Subscription;
  public selectedIndex$: Observable<number> = this.progress.tabChange.asObservable();

  // Animation
  public animationTimeout: any;
  public animationTime: number = 500;
  public allowTable = false;
  public isCopied: boolean = false;
  public error = false;

  constructor(
    private progress: EstimateService,
    private snackBar: MatSnackBar
  ) {
    setTimeout(this.onSelectedIndexChange.bind(this), 10);
  }

  ngOnInit(): void {
    this.progress.enablePage(true);
    this.getCurrentValues();
    this.openSubscription();
  }

  handleSteps(store: any, inputFlag: string, doneFlag: string): void {
    this.handleStep(store.stepZero, store.stepOne, inputFlag, () => {
      this.displayStepZeroAndOne(store.stepZero.response[0], store.stepOne.response[0]);
    });
    this.handleStep(store.stepTwo, null, inputFlag, () => {
      this.displayStepTwo(store.stepTwo.response[0])
    });
    this.handleStep(store.stepFour, null, doneFlag, () => {
      this.displayStepFour();
      this.progress.dependencyPrompt();
    }, 'stepFour');
    this.handleStep(store.stepFive, null, inputFlag, () => {
      this.displayStepFive(store.stepFive.response);
    });
    this.handleStep(store.addPhase, null, inputFlag, () => {
      this.displayAddedPhase(store.addPhase.response);
      this.progress.dependencyPrompt();
    }, 'addPhase');
  }

  handleStep(stepA: any, stepB: any, statusFlag: string, action: Function, stepName = ""): void {
    if (stepA.status === 'error' || (stepB && stepB.status === 'error')) {
      if (stepName === 'stepFour') {
        this.progress.resetLevel({ level: stepName, status: 'init' })
        this.error = true;
        this.showSnackbar('Something went wrong, please re-generate');
        this.progress.resetAfterTimeFrame();
        this.progress.enablePage(true);
      } else if (stepName === 'addPhase') {
        this.progress.resetLevel({ level: stepName, status: 'init' })
        this.error = true;
        this.tabGroup.selectedIndex = 1;
        this.showSnackbar('Something went wrong, please re-generate');
        this.progress.resetAfterTimeFrame();
        this.progress.enablePage(true);
      } else {
        this.progress.enablePage(true);
      }
      return
    }
    if (stepA.status === statusFlag && (!stepB || stepB.status === statusFlag)) {
      action();
      this.progress.enablePage(true);
    }
  }

  showSnackbar(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 6000,
    });
  }

  displayStepZeroAndOne(classifierResponse: string, summaryResponse: string,): void {
    const startTag = '<HEADLINE>';
    const endTag = '</HEADLINE>';
    this.progress.projectClassification.next(classifierResponse.trim().split(','));
    this.progress.projectHeadline.next(this.extractTextBetweenTags(summaryResponse, startTag, endTag));
    this.progress.summary.next(summaryResponse.replace(this.progress.projectHeadline.getValue(), '').replace(startTag, '').replace(endTag, ''));
    this.progress.timeSensitiveDetails.next(this.extractTextBetweenTagsIntoList(this.progress.summary.getValue()));
  }

  displayStepTwo(response: string): void {
    this.progress.timeFrameEstimate.next(response.trim().split(','));
    setTimeout(() => {
      this.tabGroup.selectedIndex = 1;
    }, 10);
  }

  displayStepFour(): void {
    this.error = false;
    this.progress.prompts[4].setDisplayed();
    this.storePhaseArrays();
    this.tabGroup.selectedIndex = 2;
    setTimeout(() => {
      this.progress.initTable.next();
    }, 10);
  }

  displayStepFive(response: string | Array<string>): void {
    var data: any;
    if (typeof response === 'string') {
      data = JSON.parse(response)['milestones']
    } else {
      data = JSON.parse(response[response.length - 1])['milestones']
    }
    this.progress.setProgress({
      level: 'stepFive',
      status: 'afterOutput'
    });
    this.mergeDependencyTableData(data, this.progress.dataSource.data);
    this.mergeDependencyArrayData(data, this.progress.displayedPhaseArray.phases);
    this.progress.disableGantChart.next(false);
  }

  displayAddedPhase(phaseArray: Array<string>): void {
    const phase = phaseArray[phaseArray.length - 1]
    this.progress.prompts.slice(-1)[0].reset();
    this.progress.prompts.slice(-1)[0].setDisplayed();
    let phaseJSON: Phase = JSON.parse(phase);
    this.progress.displayedPhaseArray.phases.push(phaseJSON)
    this.progress.prepareOutputTable.next(phaseJSON);
    setTimeout(() => {
      this.tabGroup.selectedIndex = 2;
    }, 10);
    this.orderPhaseArray(this.progress.displayedPhaseArray);
  }

  getCurrentValues(): void {
    var store = this.progress.store.getValue();
    if (store.stepOne.status === 'done') {
      this.displayStepZeroAndOne(
        store.stepZero.response[0],
        store.stepOne.response[0],
      );
      this.progress.selectedProjectType.next(store.stepOne.feedback[0]);
    }
    if (store.stepTwo.status === 'done') {
      this.displayStepTwo(store.stepTwo.response[0]);
      this.progress.selectedTimeFrame = store.stepTwo.feedback[0];
    }
  }

  onSelectedIndexChange(): void {
    this.tabSubscription = this.tabGroup?.selectedIndexChange.subscribe((tabData) => {
      if (tabData === 2) {
        this.allowTable = true
      } else {
        this.allowTable = false
      }
      clearTimeout(this.animationTimeout);
      this.animationTimeout = setTimeout(
        () => {
          this.progress.tabChange.next(tabData);
        }, this.animationTime
      );
    });
  }

  // Data transformation
  storePhaseArrays(): void {
    const phaseArrayFromStore = this.progress.store.getValue().stepFour.response;
    this.progress.originalPhaseArray = this.transformPhaseArrayToPlan(phaseArrayFromStore);
    this.progress.displayedPhaseArray = JSON.parse(JSON.stringify(this.progress.originalPhaseArray));
  }

  transformPhaseArrayToPlan(phaseArray: Array<string>): Phases {
    const phases = phaseArray.map(phaseStr => JSON.parse(phaseStr));
    const plan: Phases = { phases };
    this.orderPhaseArray(plan); // Note: This function is not provided in the original code
    return plan;
  }

  mergeDependencyTableData(dependencyArray, summaryArray): Array<any> {
    return summaryArray.map(phaseObj => {
      const { phase } = phaseObj;
      const matchingObj = dependencyArray.find(obj => obj.ID === phase.id);
      this.addDependencyDataToObject(phase, matchingObj);
      return phaseObj;
    });
  }

  mergeDependencyArrayData(dependencyArray, phaseArray): Array<any> {
    return phaseArray.map(phaseObj => {
      phaseObj.phaseMilestones.forEach(milestone => {
        const matchingObj = dependencyArray.find(obj => obj.ID === milestone.milestoneId);
        this.addDependencyDataToObject(milestone, matchingObj);
      });
      return phaseObj;
    });
  }

  addDependencyDataToObject(targetObject, matchingObj): void {
    if (matchingObj) {
      targetObject['synergies'] = matchingObj.SimultaneousIDs.split(',');
      targetObject['dependencies'] = matchingObj.DependendIDs.split(',');
    }
  }

  // Helper functions
  openSubscription(): void {
    this.storeSubscription = this.progress.store.subscribe({
      next: (store) => this.handleSteps(store, 'afterInput', 'done'),
      error: (e) => console.error(e),
    });
  }

  orderPhaseArray(phaseArray: Phases): void {
    phaseArray.phases.sort((a, b) => this.compareByPhaseId(a, b));
  }

  compareByPhaseId(a, b) {
    const idA = parseFloat(a.phaseId);
    const idB = parseFloat(b.phaseId);
    return idA - idB;
  }

  extractTextBetweenTags(inputString: string, startTag: string, endTag: string): string {
    const startIndex = inputString.indexOf(startTag) + startTag.length;
    const endIndex = inputString.indexOf(endTag);

    if (startIndex >= 0 && endIndex >= 0 && endIndex > startIndex) {
      return inputString.substring(startIndex, endIndex);
    } else {
      return null; // Headline tags not found or in incorrect format
    }
  }

  extractTextBetweenTagsIntoList(input, matchRegex = /<([^>]+)>(.*?)<\/\1>/gs) {
    const matches = input.match(matchRegex) || [];
    return matches.map((match) => match.replace(matchRegex, '$2').trim());
  }

  // Copy result functions
  copyAsText(event: any): void {
    var formattedText = "";

    this.progress.dataSource.data.forEach(item => {
      if (item.phase.id) {
        formattedText += "ID: " + item.phase.id + "\n";
      }

      if (item.phase.name) {
        formattedText += "Name: " + item.phase.name + "\n";
      }

      if (item.phase.risk) {
        formattedText += "Risk: " + item.phase.risk + "\n";
      }

      if (item.phase.level) {
        formattedText += "Risk Description: " + item.phase.level + "\n";
      }

      if (item.phase.time) {
        formattedText += "Time: " + item.phase.time + "\n";
      }

      if (item.phase.details) {
        formattedText += "Details: " + item.phase.details + "\n";
      }

      formattedText += "\n";
    });

    this.showSnackbar('Copying to clipboard was successful!');

    // Copying to clipboard
    navigator.clipboard.writeText(formattedText).then(function () {
      console.log('Copying to clipboard was successful!');
    }, function (err) {
      console.error('Failed to copy text: ', err);
    });
    this.isCopied = true;
  }

  ngOnDestroy(): void {
    clearTimeout(this.animationTimeout);
    this.storeSubscription.unsubscribe();
    this.tabSubscription.unsubscribe();
  }
}
